import type { GetStaticProps, NextPage } from 'next'
import React from 'react'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { seoImageLoader } from '@portfolio/components'
import { DEFAULT_REVALIDATE_TIME, ROUTE_BASE_ARTICLE } from '@portfolio/configs'
import { PageBuilderSection, PageResponse } from '@portfolio/models'
import { DefaultPage } from '@portfolio/page'
import {
  getRouteByPath,
  getSiteConfiguration,
} from '@portfolio/services/sanity-service'

const InsightsPage: NextPage<PageResponse<PageBuilderSection>> = (props) => {
  const { seo, disallowRobots, path } = props
  const { asPath } = useRouter()

  const canonicalPath = asPath?.split('?')[0] || path || ROUTE_BASE_ARTICLE
  const canonicalUrl = `${process.env.BASE_URL}${canonicalPath}`
  const seoImage = seoImageLoader(seo?.ogImage, seo?.ogAltImage)

  return (
    <>
      {seo ? (
        <NextSeo
          titleTemplate={seo?.titleTemplate}
          nofollow={disallowRobots}
          noindex={disallowRobots}
          title={seo.metaTitle}
          description={seo.metaDescription}
          canonical={canonicalUrl}
          openGraph={{
            url: canonicalUrl,
            type: 'website',
            title: seo.ogTitle,
            description: seo.ogDescription,
            images: [...(seoImage ? [seoImage] : [])],
          }}
          twitter={{
            cardType: 'summary_large_image',
          }}
        />
      ) : (
        <NextSeo
          noindex={disallowRobots}
          nofollow={disallowRobots}
          canonical={canonicalUrl}
        />
      )}
      <DefaultPage {...props} />
    </>
  )
}

export const getStaticProps: GetStaticProps<
  PageResponse<PageBuilderSection[]>
> = async ({ locale, preview = false }) => {
  const siteConfiguration = await getSiteConfiguration(locale, preview)
  const pageData = await getRouteByPath(ROUTE_BASE_ARTICLE, locale, preview)

  if (!pageData) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...pageData,
      siteConfiguration,
    },
    revalidate: DEFAULT_REVALIDATE_TIME,
  }
}

export default InsightsPage
